import { useEffect, useMemo, useState } from 'react';
import { app, authentication } from '@microsoft/teams-js';

import {
    Dropdown,
    Textarea,
    Option,
    Label,
    Checkbox,
    MessageBar,
    MessageBarBody,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useSelector } from '../../store/hooks';
import {
    getConnectToTeams,
    selectHelp,
    selectTeam,
    setIsSubmitting,
    setSimulateFailedConnection,
    updateMessage,
} from '../../store/features/help/helpSlice';

import { submitDialog } from '../../utils/teams';
import { useHelpPageStyles } from './HelpPage.styles';
import { IBotSubmitPayload } from '../../model/IBotSubmitPayload';
import IBrokerConnectionHelpData from '../../model/brokerConnections/IBrokerConnectionFlightHelpData';
import INewConversationData from '../../model/brokerConnections/INewConversationData';
import BotSubmitAction from '../../model/BotSubmitAction';
import {
    loadConfig,
    selectConfig,
} from '../../store/features/config/configSlice';
import { FeatureFlags } from '../../model/Config';
import { useSearchParams } from 'react-router';
import { getBrokerConnectionDetails } from '../../store/features/help/helpAPI';
import SubmitButton from '../../controls/Buttons/SubmitButton';

interface HelpPageProps {
    mode?: 'help' | 'reopen';
}

const HelpPage = ({ mode = 'help' }: HelpPageProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const styles = useHelpPageStyles();

    const [searchParams] = useSearchParams();
    const teamId = searchParams.get('teamId');
    const channelId = searchParams.get('channelId');
    const brokerConnectionId = searchParams.get('brokerConnectionId');
    const rated = searchParams.get('rated') === 'true';

    const isReopenMode = mode === 'reopen';

    const {
        connectToTeamsStatus,
        connectToTeams,
        selectedConnectToTeam,
        message,
        isSubmitting,
        simulateFailedConnection,
    } = useSelector(selectHelp);

    const { featureFlags } = useSelector(selectConfig);
    const allowFailedConnectionSimulation = useMemo(
        () =>
            featureFlags.includes(FeatureFlags.allowFailedConnectionSimulation),
        [featureFlags]
    );

    const [forceDisableDropdown, setForceDisableDropdown] = useState(false);
    const [originalTeamId, setOriginalTeamId] = useState<string | null>(null);
    const [originalChannelId, setOriginalChannelId] = useState<string | null>(
        null
    );
    const [
        isOriginalConnectionSystemGenerated,
        setIsOriginalConnectionSystemGenerated,
    ] = useState(false);
    const [isLoadingOriginalConnection, setIsLoadingOriginalConnection] =
        useState(false);

    useEffect(() => {
        dispatch(getConnectToTeams());
        dispatch(loadConfig());
    }, [dispatch]);

    useEffect(() => {
        if (connectToTeamsStatus === 'idle') {
            app.notifySuccess();
        }
    }, [connectToTeamsStatus]);

    useEffect(() => {
        if (
            !isReopenMode ||
            !brokerConnectionId ||
            !connectToTeams ||
            connectToTeams.length === 0
        ) {
            return;
        }

        const fetchOriginalConnection = async () => {
            try {
                setIsLoadingOriginalConnection(true);
                const connection =
                    await getBrokerConnectionDetails(brokerConnectionId);
                setOriginalTeamId(connection.groundTeamId);
                setOriginalChannelId(connection.groundChannelId);
                setIsOriginalConnectionSystemGenerated(
                    connection.isSystemGenerated ?? false
                );

                const foundTeam = connectToTeams?.find(
                    (x) => x.channelId === connection.groundChannelId
                );
                if (foundTeam) {
                    dispatch(selectTeam(foundTeam.key));
                }
            } catch (error) {
                console.error('Error fetching original connection:', error);
            } finally {
                setIsLoadingOriginalConnection(false);
            }
        };

        fetchOriginalConnection();
    }, [isReopenMode, brokerConnectionId, connectToTeams, dispatch]);

    useEffect(() => {
        if (isReopenMode) {
            return;
        }

        if (!connectToTeams) {
            setForceDisableDropdown(false);
            return;
        }

        let dropdownDisabled = true;

        let foundTeam = connectToTeams?.find((x) => x.channelId === channelId);
        if (!foundTeam) {
            foundTeam = connectToTeams?.find((x) => x.teamId === teamId);
            dropdownDisabled = false;
        }

        if (foundTeam) {
            dispatch(selectTeam(foundTeam.key));
        }

        setForceDisableDropdown(dropdownDisabled);
    }, [
        isReopenMode,
        isOriginalConnectionSystemGenerated,
        teamId,
        channelId,
        connectToTeams,
        setForceDisableDropdown,
        dispatch,
    ]);

    const isSameTeam = useMemo(() => {
        if (
            !isReopenMode ||
            !selectedConnectToTeam ||
            !originalTeamId ||
            !originalChannelId
        ) {
            return false;
        }
        return (
            selectedConnectToTeam.teamId === originalTeamId &&
            selectedConnectToTeam.channelId === originalChannelId
        );
    }, [
        isReopenMode,
        selectedConnectToTeam,
        originalTeamId,
        originalChannelId,
    ]);

    const showMessageField = useMemo(() => {
        if (!isReopenMode) {
            return true;
        }

        if (isLoadingOriginalConnection) {
            return false;
        }

        return !isSameTeam;
    }, [isReopenMode, isLoadingOriginalConnection, isSameTeam]);

    const isSubmitDisable = useMemo(() => {
        const disable = isSubmitting || !selectedConnectToTeam;
        if (disable) {
            return true;
        }

        if (!showMessageField) {
            return false;
        }

        const text = message?.trim() || '';
        return text.length === 0 || text.length > 4000;
    }, [isSubmitting, selectedConnectToTeam, message, showMessageField]);

    const handleSubmit = async () => {
        if (isSubmitting) {
            return;
        }

        const token = await authentication.getAuthToken();
        dispatch(setIsSubmitting(true));
        const text = message?.trim() || '';

        if (isReopenMode) {
            const submitRequest: IBotSubmitPayload<INewConversationData> = {
                data: {
                    originalBrokerConnectionId: brokerConnectionId!,
                    connectTo: selectedConnectToTeam!,
                    message: text!,
                    simulateFailedConnection,
                },
                appUserAccessToken: token,
                action: BotSubmitAction.NewConversation,
            };
            submitDialog(submitRequest);
        } else {
            const submitRequest: IBotSubmitPayload<IBrokerConnectionHelpData> =
                {
                    data: {
                        connectTo: selectedConnectToTeam!,
                        message: text!,
                        simulateFailedConnection,
                    },
                    appUserAccessToken: token,
                    action: BotSubmitAction.BrokerConnectionFlightHelp,
                };
            submitDialog(submitRequest);
        }
    };

    return (
        <div className={styles.root}>
            {isReopenMode && rated && (
                <MessageBar intent="success" style={{ marginBottom: '16px' }}>
                    <MessageBarBody>
                        {t('Rate_Conversation_MessageBar')}
                    </MessageBarBody>
                </MessageBar>
            )}
            <div className={styles.formField}>
                <Label weight="semibold" required>
                    {t('ConnectTo')}
                </Label>
                <Dropdown
                    value={selectedConnectToTeam?.title || ''}
                    defaultValue={
                        connectToTeams.find((t) => t.isContactable === true)
                            ?.key
                    }
                    disabled={
                        (isReopenMode && isLoadingOriginalConnection) ||
                        (isReopenMode && isOriginalConnectionSystemGenerated) ||
                        (!isReopenMode && forceDisableDropdown) ||
                        connectToTeamsStatus !== 'idle'
                    }
                    placeholder={t('SelectTeamPlaceholder')!}
                    onOptionSelect={(_, data) => {
                        dispatch(selectTeam(data.optionValue));
                    }}
                >
                    {connectToTeams
                        .filter((t) => t.isContactable === true)
                        .map((i) => (
                            <Option key={i.key} value={i.key} text={i.title}>
                                {i.title}
                            </Option>
                        ))}
                </Dropdown>
            </div>
            {showMessageField && (
                <div className={styles.formField}>
                    <Label weight="semibold" required>
                        {t('Message')}
                    </Label>
                    <Textarea
                        rows={5}
                        className={styles.textArea}
                        value={message}
                        onChange={(_, data) => {
                            dispatch(updateMessage(data?.value));
                        }}
                    ></Textarea>
                </div>
            )}
            {allowFailedConnectionSimulation && (
                <Checkbox
                    label={
                        <Label weight="semibold">
                            {t('SimulateFailedConnection')}
                        </Label>
                    }
                    onChange={(_, data) => {
                        dispatch(
                            setSimulateFailedConnection(data.checked === true)
                        );
                    }}
                />
            )}

            <div className={styles.actions}>
                <SubmitButton
                    onSubmit={handleSubmit}
                    disabled={isSubmitDisable || isSubmitting}
                />
            </div>
        </div>
    );
};

export default HelpPage;
